/**
 * Parsing and composing the colour strings the builders actually store.
 *
 * The picker used to understand exactly two forms — 6- and 8-digit hex — while
 * the corpus stores four: `#rrggbb`, `#rrggbbaa`, `rgba(...)` (81 text/border
 * inks) and the literal token `transparent` (2683 backgrounds). Everything it
 * could not parse was normalised to `#FFFFFF` on open.
 *
 * React-free and dependency-free on purpose: it is unit-testable without a
 * mount, and `parse-email.ts` may eventually want the same parser.
 */

export type Rgba = { r: number; g: number; b: number; a: number };

/**
 * The literal token, not a colour value. `Title.tsx`, `Paragraph.tsx` and four
 * sites in `parse-email.ts` compare against it by identity to decide whether to
 * emit a background at all, so it must survive a round-trip as itself — a
 * numerically equivalent `#FFFFFF00` would turn 2683 transparent backgrounds
 * into opaque white in the canvas and into a dropped-or-black declaration in
 * Outlook.
 */
export const TRANSPARENT = "transparent";

/**
 * Deliberately small: the corpus uses NO named colour other than `transparent`
 * (verified by scanning every preset prop). These are here only so an imported
 * or AI-authored tree does not fall off the parser. A canvas `fillStyle`
 * round-trip would cover all 148 CSS names but jsdom does not implement it, so
 * the gate would pass in tests and fail in the browser.
 */
const NAMED: Record<string, [number, number, number]> = {
  black: [0, 0, 0],
  silver: [192, 192, 192],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  white: [255, 255, 255],
  maroon: [128, 0, 0],
  red: [255, 0, 0],
  purple: [128, 0, 128],
  fuchsia: [255, 0, 255],
  green: [0, 128, 0],
  lime: [0, 255, 0],
  olive: [128, 128, 0],
  yellow: [255, 255, 0],
  navy: [0, 0, 128],
  blue: [0, 0, 255],
  teal: [0, 128, 128],
  aqua: [0, 255, 255],
};

const clamp255 = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

/** A channel that may be `0-255` or a percentage. */
const channel = (raw: string): number =>
  raw.trim().endsWith("%")
    ? clamp255((parseFloat(raw) / 100) * 255)
    : clamp255(parseFloat(raw));

/** Alpha that may be `0-1` or a percentage. */
const alpha = (raw: string | undefined): number => {
  if (raw === undefined) return 1;
  const v = raw.trim().endsWith("%") ? parseFloat(raw) / 100 : parseFloat(raw);
  return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 1;
};

/**
 * Every form the builders can store, plus the ones a hand-edited or imported
 * tree can introduce. Returns null for anything unrecognised — callers must
 * treat that as "leave the value alone", never as "substitute a default".
 */
export function parseCssColor(input: string | undefined | null): Rgba | null {
  if (typeof input !== "string") return null;
  const s = input.trim();
  if (s.length === 0) return null;

  if (s.toLowerCase() === TRANSPARENT) return { r: 0, g: 0, b: 0, a: 0 };

  const named = NAMED[s.toLowerCase()];
  if (named) return { r: named[0], g: named[1], b: named[2], a: 1 };

  const hex = s.startsWith("#") ? s.slice(1) : null;
  if (hex && /^[0-9a-f]+$/i.test(hex)) {
    const dup = (c: string) => parseInt(c + c, 16);
    if (hex.length === 3) return { r: dup(hex[0]), g: dup(hex[1]), b: dup(hex[2]), a: 1 };
    if (hex.length === 4)
      return { r: dup(hex[0]), g: dup(hex[1]), b: dup(hex[2]), a: dup(hex[3]) / 255 };
    if (hex.length === 6)
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: 1,
      };
    if (hex.length === 8)
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: parseInt(hex.slice(6, 8), 16) / 255,
      };
    return null;
  }

  // `rgb(1,2,3)` / `rgba(1 2 3 / 50%)` — comma AND space syntax, both legal.
  const fn = s.match(/^rgba?\(([^)]*)\)$/i);
  if (fn) {
    const parts = fn[1].split("/");
    const rgb = parts[0].trim().split(/[\s,]+/).filter(Boolean);
    if (rgb.length < 3) return null;
    const a = parts.length > 1 ? alpha(parts[1]) : alpha(rgb[3]);
    const [r, g, b] = [channel(rgb[0]), channel(rgb[1]), channel(rgb[2])];
    if ([r, g, b].some((n) => !Number.isFinite(n))) return null;
    return { r, g, b, a };
  }

  return null;
}

const hex2 = (n: number): string => clamp255(n).toString(16).padStart(2, "0").toUpperCase();

export const toHex = ({ r, g, b }: { r: number; g: number; b: number }): string =>
  `#${hex2(r)}${hex2(g)}${hex2(b)}`;

/**
 * How a picked colour is written back.
 *
 * `"email"` is the A4 (email + document) policy. 8-digit hex is CSS Color 4:
 * Outlook's Word engine cannot parse it, and `directionTheme.ts` states the
 * house rule that translucency must not be expressed that way. So full opacity
 * emits plain hex, zero emits the literal `transparent` token every consumer
 * already special-cases, and anything between emits `rgba()` — which Outlook
 * *drops* (no background) rather than mis-renders (black).
 *
 * `"css"` keeps the website builder on 8-digit hex, whose runtime is a real
 * browser and whose stored values are already in that form.
 */
export function composePickerColor(
  color: string,
  opacity: number,
  mode: "email" | "css",
): string {
  if (opacity >= 100) return color;

  if (mode === "css") {
    return `${color}${hex2((opacity * 255) / 100)}`;
  }

  if (opacity <= 0) return TRANSPARENT;

  const rgb = parseCssColor(color);
  if (!rgb) return color; // unparseable: leave it alone rather than corrupt it
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${Number((opacity / 100).toFixed(2))})`;
}
